import { token } from '@atlaskit/tokens';
import type { ConnectCallback } from '../../../types.tsx';
import type { DialogOptions, ShowJqlEditorOptions, ShowJqlEditorResult } from './types.tsx';

/**
 * This is copied from `jira-components/jira-plugins/auiplugin/imported_node_modules/@atlassian/aui/src/js/aui/escape-html.js`.
 * The implementation is similar to `lodash.escape`
 */
const escapeHTML = (str: string) => {
	const special: Record<string, string> = {
		'<': '&lt;',
		'>': '&gt;',
		'&': '&amp;',
		"'": '&#39;',
		'`': '&#96;',
	};

	return str.replace(/[&"'<>`]/g, (char) => {
		if (typeof special[char] === 'string') {
			return special[char];
		}

		return '&quot;';
	});
};

const TEMPLATES = {
	/**
	 * JQL Editor Dialog container
	 */
	dialog: (options: DialogOptions) => {
		const sectionId = escapeHTML(options.sectionId || '');
		const header = escapeHTML(options.header || '');
		const src = escapeHTML(options.src || '');
		const frameId = escapeHTML(options.frameId || '');
		const submitButtonId = escapeHTML(options.submitButtonId || '');
		const cancelButtonId = escapeHTML(options.cancelButtonId || '');
		const descriptionText = escapeHTML(options.descriptionText || '');
		const submitText = escapeHTML(options.submitText || '');
		const cancelText = escapeHTML(options.cancelText || '');

		const html = `
<section id="${sectionId}" class="aui-dialog2 aui-dialog2-xlarge aui-layer" data-aui-remove-on-hide="true" role="dialog" aria-hidden="true">
    <header class="aui-dialog2-header">
        <h2>${header}</h2>
    </header>
    <div style="height: 100%; overflow: hidden; display: flex; flex-direction: column; background-color: ${token('elevation.surface.overlay')};">
        <p>${descriptionText}</p>
        <iframe src="${src}" id="${frameId}" style="flex: 1; border: none;"></iframe>
    </div>
    <footer class="aui-dialog2-footer">
        <div class="aui-dialog2-footer-actions">
            <button id="${submitButtonId}" class="aui-button aui-button-primary">
                ${submitText}
            </button>
            <button id="${cancelButtonId}" class="aui-button aui-button-link">
                ${cancelText}
            </button>
        </div>
    </footer>
</section>
            `;

		return html.trim();
	},
};

const frameId = 'connect-app-jql-editor-frame-id';
const sectionId = 'connect-app-jql-editor-section-id';
const submitButtonId = 'connect-app-jql-editor-submit-id';
const cancelButtonId = 'connect-app-jql-editor-cancel-id';

/**
 * Prepares the JQL Editor dialog in preparation for fast rendering.
 * This method should be called on iframe load if it contains a JQL editor trigger.
 * @memberof module:Jira
 * @method initJQLEditor
 * @noDemo
 * @example
 * AP.jira.initJQLEditor();
 */
// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
export const initJQLEditor = () => {};

/**
 * Launches a JQL Editor dialog. A callback will be invoked when the JQL is submitted by the user.
 *
 * @memberof module:Jira
 * @method showJQLEditor
 * @param {Function} callback - invoked when dialog is submitted, includes an object containing the jql
 * @param {Object} options - contains data to pre-fill the dialog with
 * @param {string} options.jql - Optionally provide initial JQL to pre-fill the dialog with
 * @param {string} options.header - Optionally overwrite the title of the dialog
 * @param {string} options.descriptionText - Optionally provide up to 512 chars to be displayed below header
 * @param {string} options.submitText - Optionally overwrite the submit button text
 * @param {string} options.cancelText - Optionally overwrite the cancel button text
 * @noDemo
 * @example
 * var options = {
 *   jql: 'project = ACJS',
 *   header: 'My title',
 *   descriptionText: 'My custom description text for dialog',
 *   submitText: 'My submit label',
 *   cancelText: 'My cancel label'
 * };
 *
 * var callback = function(obj) {
 *   alert(obj.jql);
 * };
 *
 * AP.jira.showJQLEditor(options, callback);
 */
export const showJQLEditor = (
	options: ShowJqlEditorOptions,
	callback: ConnectCallback<(result: ShowJqlEditorResult) => void>,
) => {
	const src = `/issues/?app=true&jql=${options.jql || ''}`;

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const container = document.createElement('div');

	container.innerHTML = TEMPLATES.dialog({
		src,
		frameId,
		sectionId,
		submitButtonId,
		cancelButtonId,
		header: options.header || 'JQL Editor',
		submitText: options.submitText || 'Submit',
		cancelText: options.cancelText || 'Cancel',
		descriptionText: (options.descriptionText || '').substring(0, 512),
	});
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion, jira/jira-ssr/no-unchecked-globals-usage
	document.body.appendChild(container.firstChild!);

	const onCancel = (e: MouseEvent) => {
		e.preventDefault();
		AJS.dialog2(`#${sectionId}`).hide();
	};

	const onSubmit = (e: MouseEvent) => {
		e.preventDefault();
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-non-null-assertion, jira/jira-ssr/no-unchecked-globals-usage
		const frameDocument = (document.getElementById(frameId) as HTMLIFrameElement).contentWindow!
			.document;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		(frameDocument.querySelector('.search-button') as HTMLButtonElement).click();
		const jql = new URLSearchParams(frameDocument.location.search).get('jql');
		AJS.dialog2(`#${sectionId}`).hide();
		callback({ jql });
	};

	AJS.dialog2(`#${sectionId}`).show();

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	document.getElementById(cancelButtonId)?.addEventListener('click', onCancel);

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	document.getElementById(submitButtonId)?.addEventListener('click', onSubmit);
};
